import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Script from 'next/script';
import '../styles/globals.css';
import OneTapLogin from '../components/OneTapLogin';

const GTM_ID = 'GTM-KC5G5WLD';

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const logoutProcessed = useRef(false);
  const [isClient, setIsClient] = useState(false);
  const gtmInitialized = useRef(false);

  // Ensure client-side only rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Route change tracking
  useEffect(() => {
    const handleRouteChangeComplete = (url) => {
      if (typeof window !== 'undefined') {
        window.manualPageViewSentThisLoad = false;
        console.log('[ROUTER] Route changed to:', url);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  // Logout logic
  useEffect(() => {
    if (typeof window === 'undefined' || logoutProcessed.current) return;

    const forceLogout = localStorage.getItem('force_logout') === 'true';
    const logoutParam = router.query.logout === 'true';

    if (forceLogout || logoutParam) {
      logoutProcessed.current = true;
      localStorage.setItem('last_logout_time', Date.now().toString());
      localStorage.setItem('manual_logout', 'true');
      setTimeout(() => localStorage.removeItem('force_logout'), 2000);

      localStorage.removeItem('auth_action');
      localStorage.removeItem('user-storage');
      sessionStorage.removeItem('backend_verified');
      const cookiesToClear = ['jwt_access_token', 'jwt_refresh_token', 'auth_token', 'firebase_token'];
      cookiesToClear.forEach(name => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      });
    }
  }, [router.query.logout]);

  const handleGtmScriptLoad = () => {
    console.log('[GTM] Script loaded successfully');
  };

  const handleGtmScriptError = () => {
    console.error('[GTM] Error loading GTM script');
    gtmInitialized.current = false;
  };

  return (
    <>
      <Head>
        <title>My App</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="preconnect" href="https://www.google-analytics.com" /> 
      </Head>

      {isClient && (
        <Script
          id="gtm-init-and-load"
          strategy="afterInteractive"
          onLoad={handleGtmScriptLoad}
          onError={handleGtmScriptError}
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                // Prevent multiple initialization
                if (window.gtmInitialized) return;
                window.gtmInitialized = true;

                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                window.gtag = gtag;

                // Get geo data from middleware cookie
                function getCookie(name) {
                  if (typeof document === 'undefined') return null;
                  const value = "; " + document.cookie;
                  const parts = value.split("; " + name + "=");
                  if (parts.length === 2) {
                    try {
                      return decodeURIComponent(parts.pop().split(";").shift());
                    } catch (e) {
                      return parts.pop().split(";").shift();
                    }
                  }
                  return null;
                }

                function isStrictPrivacyRegion() {
                  try {
                    const geoCookie = getCookie('user-geo');
                    if (!geoCookie) return true; // Default strict if no geo data
                    const geoData = JSON.parse(geoCookie);
                    return geoData.strict === true;
                  } catch (e) {
                    return true; // Default strict on error
                  }
                }

                // Store geo info for ConsentManager
                window.isStrictPrivacyRegion = isStrictPrivacyRegion();
                
                window.initialAnalyticsDefaultState = 'denied'; // Always denied by default for Consent Mode 2.0
                window.manualPageViewSentThisLoad = false; 

                // Consent Mode 2.0: Always start with DENIED for all tracking
                // Only grant after explicit user consent via ConsentManager
                const consentDefaults = {
                  'functionality_storage': 'granted',   // Always allowed - essential
                  'security_storage': 'granted',        // Always allowed - essential  
                  'personalization_storage': 'granted', // Always allowed - essential
                  'analytics_storage': 'denied',        // Requires consent
                  'ad_storage': 'denied',               // Requires consent
                  'ad_user_data': 'denied',             // Requires consent  
                  'ad_personalization': 'denied',       // Requires consent
                  'wait_for_update': 500                // Wait for ConsentManager
                };

                gtag('consent', 'default', consentDefaults);

                // Consent Mode 2.0 settings - always redact until consent given
                gtag('set', 'ads_data_redaction', true);
                gtag('set', 'url_passthrough', true);

                // Load GTM script only once
                if (!window.gtmScriptLoaded) {
                  window.gtmScriptLoaded = true;
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','${GTM_ID}');
                }

                console.log('[GTM] Initialized with defaults:', consentDefaults);
              })();
            `
          }}
        />
      )}

      <Component {...pageProps} />
      <OneTapLogin />

      {isClient && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
            title="GTM noscript fallback"
          />
        </noscript>
      )}
    </>
  );
}

export default MyApp;